import { useState } from "react";
import { Loader2, Gift, X } from "lucide-react";
import { ReflectedToggle } from "./ReflectedToggle";

/**
 * PromoLiquidateDialog — replaces the bare window.confirm when liquidating
 * a plan with its active promo. Lets the admin state whether the money is
 * already reflected in the account.
 *
 * target: { plan, promo }  ·  onConfirm(reflected) → Promise
 */
export const PromoLiquidateDialog = ({ target, onClose, onConfirm }) => {
  const [reflected, setReflected] = useState(true);
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const { plan, promo } = target;
  const cur = plan.currency || "MXN";
  const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })} ${cur}`;

  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(reflected); } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[165] bg-black/85 backdrop-blur-md flex items-center justify-center px-3"
      data-testid="promo-liquidate-dialog"
      onClick={onClose}
    >
      <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a0510] to-black border-2 border-amber-500/60 rounded-2xl p-5 shadow-[0_0_60px_rgba(245,158,11,0.25)]" onClick={(e) => e.stopPropagation()}>
        <button data-testid="promo-liquidate-close" onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-1">
          <Gift className="w-5 h-5 text-amber-300" />
          <h3 className="font-serif text-lg text-white">Liquidar con promoción</h3>
        </div>
        <p className="text-xs text-neutral-400 mb-3">El plan quedará marcado como <b className="text-white">pagado</b> aplicando el descuento. Se registra el abono restante y se envía el recibo al cliente.</p>
        <div className="rounded-lg bg-black/40 border border-white/10 p-3 mb-3 text-xs space-y-1">
          <div className="text-neutral-400">Cliente: <b className="text-white">{plan.user_name}</b></div>
          <div className="text-neutral-400">Trabajo: <b className="text-white">{plan.service_name}</b></div>
          <div className="text-neutral-400">Descuento: <b className="text-emerald-300">−{fmt(promo.discount_amount)}</b> · Monto a registrar: <b className="text-amber-300" data-testid="promo-liquidate-amount">{fmt(promo.new_remaining)}</b></div>
        </div>
        <div className="mb-4">
          <ReflectedToggle value={reflected} onChange={setReflected} idPrefix="promo-liq-reflected" />
        </div>
        <button
          data-testid="promo-liquidate-confirm"
          disabled={busy}
          onClick={confirm}
          className="w-full py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest font-bold bg-amber-400 hover:bg-amber-300 text-black disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gift className="w-3.5 h-3.5" />}
          Confirmar liquidación · {fmt(promo.new_remaining)}
        </button>
      </div>
    </div>
  );
};

export default PromoLiquidateDialog;
